import React from "react";
import { Alert } from "react-bootstrap";
import styled from "styled-components";

import { WidgetTheme } from "../../types/analytics";

export type MetricCardValueFormat = "number" | "percent" | "decimal";

export type MetricCardItem = {
    key: string;
    label: string;
    value: number | null;
    changePercent?: number | null;
    numericChange?: number | null;
    valueFormat?: MetricCardValueFormat;
};

type MetricCardsProps = {
    metrics: MetricCardItem[];
    theme: WidgetTheme;
    emptyMessage?: string;
    minCardWidth?: string;
    combineChangeAndDelta?: boolean;
    noChangeLabel?: string;
};

const MetricGrid = styled.div`
    display: flex;
    gap: 16px;
    flex-wrap: nowrap;
    overflow-x: auto;
`;

const MetricCard = styled.div<{ theme: WidgetTheme }>`
    border: 1px solid ${props => props.theme.border};
    border-radius: 8px;
    padding: 16px;
    background: ${props => props.theme.chartBg};
    box-shadow: 0 1px 2px rgba(0, 0, 0, 0.25);
`;

const MetricLabel = styled.div<{ theme: WidgetTheme }>`
    font-size: 0.9rem;
    color: ${props => props.theme.text};
    opacity: 0.7;
    margin-bottom: 8px;
`;

const MetricValue = styled.div<{ theme: WidgetTheme }>`
    font-size: 1.8rem;
    font-weight: 700;
    line-height: 1.1;
    color: ${props => props.theme.text};
`;

const MetricChange = styled.div<{ $positive: boolean; $negative: boolean }>`
    margin-top: 8px;
    font-size: 0.95rem;
    font-weight: 600;
    color: ${props => props.$positive ? "#28a745" : props.$negative ? "#dc3545" : "#6c757d"};
`;

const formatNumber = (value: number | null, format: MetricCardValueFormat): string => {
    if (value === null || value === undefined) {
        return "-";
    }

    if (format === "percent") {
        return `${value.toFixed(2)}%`;
    }

    if (format === "decimal") {
        return value.toFixed(2);
    }

    return value.toLocaleString();
};

const formatSigned = (value: number | null, suffix = ""): string => {
    if (value === null || value === undefined) {
        return "-";
    }

    const sign = value > 0 ? "+" : "";
    const isWholeNumber = Number.isInteger(value);
    const formattedValue = isWholeNumber ? value.toLocaleString() : value.toFixed(2);

    return `${sign}${formattedValue}${suffix}`;
};

const MetricCards: React.FC<MetricCardsProps> = ({
    metrics,
    theme,
    emptyMessage = "No KPI data available for the selected date range.",
    minCardWidth = "220px",
    combineChangeAndDelta = false,
    noChangeLabel = "No change data"
}) => {
    if (metrics.length === 0) {
        return <Alert variant="info">{emptyMessage}</Alert>;
    }

    return (
        <MetricGrid>
            {metrics.map(metric => {
                const changePercent = metric.changePercent ?? null;
                const numericChange = metric.numericChange ?? null;
                const positive = (changePercent ?? 0) > 0;
                const negative = (changePercent ?? 0) < 0;

                let changeText = noChangeLabel;
                if (changePercent !== null) {
                    changeText = formatSigned(changePercent, "%");
                }

                if (combineChangeAndDelta && changePercent !== null && numericChange !== null) {
                    changeText = `${formatSigned(changePercent, "%")} (${formatSigned(numericChange)})`;
                }

                return (
                    <MetricCard key={metric.key} theme={theme} style={{ minWidth: minCardWidth }}>
                        <MetricLabel theme={theme}>{metric.label}</MetricLabel>
                        <MetricValue theme={theme}>{formatNumber(metric.value, metric.valueFormat || "number")}</MetricValue>
                        <MetricChange $positive={positive} $negative={negative}>
                            {changeText}
                        </MetricChange>
                    </MetricCard>
                );
            })}
        </MetricGrid>
    );
};

export default MetricCards;
